import {createElement} from 'react'
import {CheckSpinner} from '@github-ui/mergebox'
import {
  CheckCircleFillIcon,
  GitMergeIcon,
  StopIcon,
  SkipIcon,
  GitMergeQueueIcon,
  GitPullRequestClosedIcon,
  DotFillIcon,
  ClockIcon,
  ArchiveIcon,
} from '@primer/octicons-react'
import type {SessionState} from '../types/session'

export interface IconAttributes {
  icon: React.ComponentType | (() => React.ReactElement)
  color: string
  'data-testid': string
  marginTop?: string
}

export function getLeadingVisualIconAttributes(
  prState: string = 'open',
  prInMergeQueue: boolean = false,
  state?: SessionState,
  isArchived?: boolean,
): IconAttributes {
  // Check PR states first
  if (prState === 'merged') {
    return {
      icon: GitMergeIcon,
      color: 'var(--fgColor-done)' as const,
      'data-testid': 'icon-git-merge',
    }
  }

  if (prState === 'closed') {
    return {
      icon: GitPullRequestClosedIcon,
      color: 'var(--fgColor-danger)' as const,
      'data-testid': 'icon-git-pull-request-closed',
    }
  }

  if (prInMergeQueue) {
    return {
      icon: GitMergeQueueIcon,
      color: 'var(--fgColor-attention)' as const,
      'data-testid': 'icon-git-merge-queue',
    }
  }

  // Check if the task is archived next to not mask the PR state
  if (isArchived) {
    return {
      icon: ArchiveIcon,
      color: 'var(--fgColor-muted)' as const,
      'data-testid': 'icon-archive',
    }
  }

  if (state === 'queued') {
    return {
      icon: ClockIcon,
      color: 'var(--fgColor-attention)' as const,
      'data-testid': 'icon-clock',
    }
  }

  if (state === 'waiting_for_user' || state === 'completed') {
    return {
      icon: CheckCircleFillIcon,
      color: 'var(--fgColor-success)' as const,
      'data-testid': 'icon-check-circle-fill',
    }
  }

  if (state === 'failed' || state === 'timed_out') {
    return {
      icon: StopIcon,
      color: 'var(--fgColor-danger)' as const,
      'data-testid': 'icon-stop',
    }
  }

  if (state === 'in_progress') {
    return {
      // eslint-disable-next-line @github-ui/github-monorepo/no-react-create-element -- Convert to JSX Syntax. This Requires changing to a TSX file
      icon: () => createElement(CheckSpinner, {animated: true}),
      color: 'attention.fg' as const,
      'data-testid': 'icon-spinning-circle',
    }
  }

  if (state === 'idle') {
    return {
      icon: DotFillIcon,
      color: 'var(--fgColor-muted)' as const,
      'data-testid': 'icon-dot',
    }
  }

  if (state === 'cancelled') {
    return {
      icon: SkipIcon,
      color: 'var(--fgColor-muted)' as const,
      'data-testid': 'icon-skip',
    }
  }

  return {
    icon: DotFillIcon,
    color: 'var(--fgColor-muted)' as const,
    'data-testid': 'icon-idle',
  }
}
